import { useEffect } from 'react';

import type { Task } from '@/type';

import FrameImageViewerContext from './context';
import { useGetCamData, useImageCacheManager, useImageWindow } from './hook';
import type {
  FrameImageViewerContextValue,
  __CES2026__SystemCalibration,
} from './type';
import { createLabelingBoxFor2DRenderer, extrinsicToMatrix } from './util';

const FrameImageViewerProvider = ({ children }: React.PropsWithChildren) => {
  const imageCacheManager = useImageCacheManager();
  const { getCamData } = useGetCamData();
  const generateLabelingBoxListRenderer: FrameImageViewerContextValue['generateLabelingBoxListRenderer'] =
    ({ camPosition, imageSize, labelingBoxList, taskName }) => {
      const camData = getCamData()?.[taskName as '2502830'];

      if (!camPosition || !taskName || !camData || !camData[camPosition])
        return;

      return createLabelingBoxFor2DRenderer({
        camData: {
          extrinsic: camData[camPosition]?.extrinsic,
          intrinsic: {
            fx: (imageSize.width + imageSize.height) / 3.6768,
            fy: (imageSize.width + imageSize.height) / 3.6768,
            cx: imageSize.width * 0.5,
            cy: imageSize.height * 0.70480841,
            k1: 0,
            k2: 0,
            k3: 0,
            k4: 0,
            k5: 0,
            k6: 0,
            mel: 0,
            p1: 0,
            p2: 0,
            skew: 0,
          },
          lidarCalibration: {
            id: 0,
            pitch: 0,
            roll: 0,
            yaw: 0,
            x: 0,
            y: 0,
            z: 0,
          },
        },
        labelingBoxList,
        selectedLabelingBoxIdList: [],
        selectedTask: {} as Task,
      });
    };
  const __CES2026__generateLabelingBoxListRenderer: FrameImageViewerContextValue['__CES2026__generateLabelingBoxListRenderer'] =
    ({
      cameraCalibrationConfig,
      labelingBoxList,
      selectedLabelingBoxIdList,
      selectedTask,
    }) =>
      createLabelingBoxFor2DRenderer({
        camData: {
          extrinsic: extrinsicToMatrix(cameraCalibrationConfig.extrinsic),
          intrinsic: cameraCalibrationConfig.intrinsic,
          lidarCalibration: cameraCalibrationConfig.lidarList[0],
        },
        labelingBoxList,
        selectedLabelingBoxIdList,
        selectedTask,
      });
  const { openImagePopupInNewWindow, closeImageWindow } = useImageWindow();

  useEffect(() => {
    return imageCacheManager.clearImageCache;
  }, []);

  return (
    <FrameImageViewerContext.Provider<FrameImageViewerContextValue>
      value={{
        fetchImage: imageCacheManager.fetchImage,
        generateLabelingBoxListRenderer,
        __CES2026__generateLabelingBoxListRenderer,
        getImage: imageCacheManager.getImage,
        openImagePopupInNewWindow,
        closeImageWindow,
      }}
    >
      {children}
    </FrameImageViewerContext.Provider>
  );
};

FrameImageViewerProvider.displayName = 'FrameImageViewerProvider';

export default FrameImageViewerProvider;
